import React, { useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box as Wrapper, Flex, Divider, Spinner, Text } from '@tonic-ui/react';
import { Alerts } from '../../components/alert';
import ResourcesLinks from './resources-links';
import SupportAuthenticatedForm from './support-authenticated-form';
import { getC1Token } from '../../store';
import { useSendSupportRequest } from '../../services/support-api';
import SuccessPage from './success-page';
import ErrorPage from './error-page';
import { useThemeColorStyle } from '../../hooks/use-theme-color-style';

const SupportPage = (): JSX.Element => {
  const colorStyle = useThemeColorStyle();
  const { divider, backgroundColor } = colorStyle.support;
  const { t } = useTranslation();
  // For Error/Success screens
  const [customerContacts, setCustomerContacts] = useState<string>('');

  const { c1Token } = useSelector(
    (state) => ({
      c1Token: getC1Token(state, 'token') as string,
    }),
    shallowEqual
  );

  const {
    mutateAsync: sendSupportRequest,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useSendSupportRequest(c1Token);

  if (isLoading) {
    return (
      <Flex
        data-id="waiting"
        direction="row"
        align="center"
        justify="center"
        with="100%"
        height="100%"
      >
        <Text size="3xl" fontWeight="semibold" mr="2x">
          {t('support.supportPage.submissionWaiting')}
        </Text>
        <Spinner />
      </Flex>
    );
  }

  if (isSuccess) {
    return (
      <SuccessPage
        contactMethod="phone"
        customerContacts={customerContacts}
        id="support-form-success-page"
      />
    );
  }

  if (isError) {
    return <ErrorPage errorCode={error.status} />;
  }

  const handleSubmit = async (supportRequestData: SupportRequestData) => {
    await sendSupportRequest(supportRequestData);
    // contacts for success state.
    setCustomerContacts(supportRequestData.phone);
  };

  return (
    <Flex direction="row" minHeight="100%" backgroundColor={backgroundColor}>
      <Flex flex="1" direction="column">
        <Wrapper mt="16px">
          <Alerts />
        </Wrapper>
        <SupportAuthenticatedForm onSubmit={handleSubmit} />
      </Flex>
      <Divider orientation="vertical" borderColor={divider} width="2px" />
      <Flex height="100%" maxWidth="340px">
        <ResourcesLinks />
      </Flex>
    </Flex>
  );
};

SupportPage.displayName = 'SupportPage';
export default SupportPage;
